import {
  parseSpeedWalletParams,
  persistSpeedWalletContext,
  speedWalletLocale,
  type SpeedWalletParams,
} from '~/utils/speedWallet'

/**
 * Speed Wallet mini-app arrival (`?acct=…&lang=…&p_add=…`, see
 * `utils/speedWallet.ts` for the contract).
 *
 * `capture()` does the three client-side jobs the params call for:
 *  - remember the Speed account id + Lightning address in localStorage
 *    (deposit ordering reads the former; a future withdraw flow the latter);
 *  - switch the UI locale to Speed's `lang` when we support that language;
 *  - credit the visit to the Speed Wallet affiliate: the account is configured
 *    by email in the admin Speed card (`speed.affiliate_email`), so its code is
 *    fetched from the public `speed-wallet-config` endpoint and then
 *    stored exactly like a `?ref=` code (cookie + click record) — the login
 *    payload picks it up unchanged. No code configured ⇒ nothing is attributed.
 *
 * Mirrors useAuthDeepLink: instantiated in `app.vue`'s setup (useLocaleSync
 * needs a component setup context) and fired from `onMounted`, which is
 * client-only, so no `import.meta.client` gate is needed here and the composable
 * stays runnable under vitest.
 */
export function useSpeedWallet(): { capture: () => Promise<SpeedWalletParams | null> } {
  const route = useRoute()
  const { currentLocale, setLocale } = useLocaleSync()
  const { storeReferralCode, recordClick } = useReferral()
  const config = useRuntimeConfig()

  async function attributeReferral(): Promise<void> {
    const apiBase = config.public.apiBase as string | undefined
    if (!apiBase) return
    try {
      const res = await $fetch<{ affiliateCode: string | null }>(`${apiBase}/speed-wallet-config`)
      const code = res?.affiliateCode
      if (typeof code !== 'string' || code.length === 0) return
      storeReferralCode(code)
      recordClick(code)
    } catch (err: unknown) {
      // A lost referral must never affect the landing experience.
      if (import.meta.dev) console.warn('[speed-wallet] affiliate lookup failed', err)
    }
  }

  async function capture(): Promise<SpeedWalletParams | null> {
    const params = parseSpeedWalletParams(route.query)
    if (!params) return null

    persistSpeedWalletContext(params)

    // Only when Speed's language is one we ship — an unsupported `lang` (Speed
    // has e.g. Hindi) must leave whatever the user already sees alone.
    const locale = speedWalletLocale(params.lang)
    if (locale && locale !== currentLocale.value) {
      setLocale(locale).catch((err: unknown) => {
        if (import.meta.dev) console.warn('[speed-wallet] locale switch failed', err)
      })
    }

    await attributeReferral()
    return params
  }

  return { capture }
}
